import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, ChevronRightIcon } from 'lucide-react';
import { SmartLink } from '../components/SmartLink';
import { ShareButtons } from '../components/articles/ShareButtons';
import { JobChips } from '../components/careers/JobChips';
import { JobSummaryCard } from '../components/careers/JobSummaryCard';
import { ApplicationForm } from '../components/careers/ApplicationForm';
import { scrollToAnchor } from '../utils/scroll';
import { aboutKayko, getJob, jobs, whatWeOffer } from '../data/careers';
import type { Job } from '../types/career';
import { NotFound } from './NotFound';
export function CareerJob() {
  const {
    slug = ''
  } = useParams();
  const job = getJob(slug);
  if (!job) return <NotFound data-id="element-1823" data-mp="2t89i3zh9c3ve" />;
  return <JobView key={job.slug} job={job} data-id="element-1824" data-mp="2t7pwo8seexcm" />;
}
function JobView({
  job
}: {
  job: Job;
}) {
  const applyRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const [applyVisible, setApplyVisible] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const apply = () => scrollToAnchor('apply');
  useEffect(() => {
    const observe = (el: Element | null, fn: (v: boolean) => void) => {
      if (!el) return undefined;
      const o = new IntersectionObserver(([e]) => fn(e.isIntersecting));
      o.observe(el);
      return o;
    };
    const a = observe(applyRef.current, setApplyVisible);
    const h = observe(headerRef.current, setHeaderVisible);
    return () => {
      a?.disconnect();
      h?.disconnect();
    };
  }, []);
  const others = [...jobs.filter(j => j.slug !== job.slug && j.department === job.department), ...jobs.filter(j => j.slug !== job.slug && j.department !== job.department)].slice(0, 3);
  const showBar = !applyVisible && !headerVisible;
  return <main data-id="element-1825" data-mp="3di3rqkez7fyi">
      <header ref={headerRef} className="hero-surface w-full" data-id="element-1826" data-mp="3djy254smfpoc">
        <div className="mx-auto max-w-shell px-6 pb-12 pt-6 lg:pb-16" data-id="element-1827" data-mp="38k28nzhjvgn1">
          <nav aria-label="Breadcrumb" data-id="element-1828" data-mp="1cg5l97wxl0m7">
            <ol className="flex flex-wrap items-center gap-1.5 text-[13px] text-ink-muted" data-id="element-1829" data-mp="1ch6zfl98u385">
              <li data-id="element-1830" data-mp="1hfiau0f6slsh">
                <SmartLink href="/careers" className="transition-colors duration-150 ease-out hover:text-ink" data-id="element-1831" data-mp="1hgtapp5ih9eb">
                  Careers
                </SmartLink>
              </li>
              <li aria-hidden="true" data-id="element-1832" data-mp="1qgyhbx2vs3rf">
                <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-1833" data-mp="1vehuetnueahs" />
              </li>
              <li aria-current="page" className="font-medium text-ink" data-id="element-1834" data-mp="1vcdvkq4sw1od">
                {job.department}
              </li>
            </ol>
          </nav>
          <div className="mt-10 flex flex-col gap-8 lg:mt-14 lg:flex-row lg:items-end lg:justify-between" data-id="element-1835" data-mp="2541icdqewuji">
            <div className="max-w-[760px]" data-id="element-1836" data-mp="2a2cskpkmd729">
              <h1 className="display text-[36px] font-semibold text-ink sm:text-[48px] lg:text-[56px]" data-id="element-1837" data-mp="2a578c3sou3pc">{job.title}</h1>
              <p className="mt-4 text-[18px] leading-[1.5] text-ink-soft" data-id="element-1838" data-mp="2j3szgnc86r1q">{job.summary}</p>
              <JobChips job={job} className="mt-6" data-id="element-1839" data-mp="7gpj5i78k8d3" />
            </div>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center lg:flex-col lg:items-end" data-id="element-1840" data-mp="7i0mxi0olg47">
              <button type="button" onClick={apply} className="inline-flex h-12 items-center justify-center rounded-[7px] bg-brand px-8 text-[15px] font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-hover" data-id="element-1841" data-mp="2jqp0znko1ew">
                Apply now
              </button>
              <ShareButtons title={`${job.title} at Kayko`} data-id="element-1842" data-mp="m5d9na285rw6" />
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-shell grid-cols-1 gap-12 px-6 py-12 lg:grid-cols-[minmax(0,720px)_320px] lg:justify-between lg:py-16" data-id="element-1843" data-mp="hdls1p11qwi6">
        <article className="min-w-0 space-y-10" data-id="element-1844" data-mp="19v7lppepzcfd">
          <JobSection title="About Kayko" data-id="element-1845" data-mp="19vziv4nyti7r">
            <p data-id="element-1846" data-mp="19x0wngwd8it6">{aboutKayko}</p>
          </JobSection>
          <JobSection title="About the role" data-id="element-1847" data-mp="153pkd08sb9tv">
            {job.aboutRole.map(p => <p key={p} data-id="element-1848" data-mp="onx76rumjnfm">{p}</p>)}
          </JobSection>
          <JobSection title="What you’ll do" data-id="element-1849" data-mp="to1m4dfvwa3q">
            <BulletList items={job.responsibilities} data-id="element-1850" data-mp="touneyw2q7eh" />
          </JobSection>
          <JobSection title="What you’ll bring" data-id="element-1851" data-mp="fp225j3bgjdw">
            <BulletList items={job.requirements} data-id="element-1852" data-mp="fqd6pl4l5v62" />
          </JobSection>
          <JobSection title="Nice to have" data-id="element-1853" data-mp="knwtxaj9hia5">
            <BulletList items={job.niceToHave} data-id="element-1854" data-mp="18cxsm62alf28" />
          </JobSection>
          <JobSection title="What we offer" data-id="element-1855" data-mp="18dr2hfwjskol">
            <BulletList items={whatWeOffer} check data-id="element-1856" data-mp="1cf2kaz96k1m8" />
          </JobSection>
        </article>

        <div className="hidden lg:block" data-id="element-1857" data-mp="yneefdpiy9dp">
          <div className="sticky top-[104px]" data-id="element-1858" data-mp="ymmq8ny9bdxg">
            <JobSummaryCard job={job} onApply={apply} data-id="element-1859" data-mp="13lzd38p4ol01" />
          </div>
        </div>
      </div>

      <section id="apply" ref={applyRef} className="w-full scroll-mt-[96px] bg-canvas-warm py-16 lg:py-20" aria-labelledby="apply-heading" data-id="element-1860" data-mp="1rbtl9cyi5ldy">
        <div className="mx-auto max-w-[880px] px-6" data-id="element-1861" data-mp="1w4mplzq26rpi">
          <h2 id="apply-heading" className="display text-[30px] font-semibold text-ink sm:text-[40px]" data-id="element-1862" data-mp="25gahvxmjzprz">
            Apply for this role
          </h2>
          <p className="mt-3 text-[16px] text-ink-soft" data-id="element-1863" data-mp="21dxlhztp547v">
            Takes about five minutes. Apply by {job.applyBy}.
          </p>
          <div className="mt-8" data-id="element-1864" data-mp="2a7shoicubkbd">
            <ApplicationForm jobTitle={job.title} data-id="element-1865" data-mp="2a8lr5r36onx7" />
          </div>
        </div>
      </section>

      <section className="w-full bg-white py-20" aria-labelledby="other-roles-heading" data-id="element-1866" data-mp="1miztj82gmgrb">
        <div className="mx-auto max-w-shell px-6" data-id="element-1867" data-mp="1ii7jiclrk7tz">
          <div className="flex items-end justify-between gap-4" data-id="element-1868" data-mp="1wgnr05rga2d6">
            <h2 id="other-roles-heading" className="display text-[30px] font-semibold text-ink sm:text-[40px]" data-id="element-1869" data-mp="1whysfyxfay14">
              Other open roles
            </h2>
            <SmartLink href="/careers" className="hidden text-[15px] font-semibold text-brand hover:text-brand-hover sm:inline" data-id="element-1870" data-mp="37v1ayzj31xnx">
              See all roles
            </SmartLink>
          </div>
          <ul className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3" data-id="element-1871" data-mp="3ay72v59vcc8v">
            {others.map(o => <li key={o.slug} data-id="element-1872" data-mp="3ey93db3vazmk">
                <SmartLink href={`/careers/${o.slug}`} className="group flex h-full flex-col rounded-[14px] border border-canvas-line p-6 transition-colors duration-150 ease-out hover:border-ink/25" data-id="element-1873" data-mp="3ex7iyg0yp0s6">
                  <span className="text-[13px] font-medium text-ink-muted" data-id="element-1874" data-mp="2r9y9wt0i428b">{o.department}</span>
                  <span className="mt-2 text-[18px] font-semibold tracking-[-0.02em] text-ink group-hover:text-brand" data-id="element-1875" data-mp="2w7zvplpbafnh">{o.title}</span>
                  <span className="mt-2 text-[14px] text-ink-soft" data-id="element-1876" data-mp="316udckbjly60">
                    {o.location} · {o.mode} · {o.type}
                  </span>
                  <span className="mt-auto inline-flex items-center gap-1.5 pt-5 text-[14px] font-semibold text-brand" data-id="element-1877" data-mp="28iyjroh9smdg">
                    View role
                    <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-1878" data-mp="2dggsbdrdkzkx" />
                  </span>
                </SmartLink>
              </li>)}
          </ul>
        </div>
      </section>

      <div className={`fixed inset-x-0 bottom-0 z-30 border-t border-canvas-line bg-white/95 px-4 py-3 backdrop-blur transition-[transform,opacity] duration-200 ease-out lg:hidden ${showBar ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-full opacity-0'}`} style={{
      paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))'
    }} data-id="element-1879" data-mp="1vzjlunxsjhaj">
        <div className="flex items-center gap-3" data-id="element-1880" data-mp="1d9bpcooljp3b">
          <div className="min-w-0 flex-1" data-id="element-1881" data-mp="1da4pv7wypid0">
            <p className="truncate text-[14px] font-semibold text-ink" data-id="element-1882" data-mp="18ba5w2n9dnrb">{job.title}</p>
            <p className="truncate text-[12px] text-ink-muted" data-id="element-1883" data-mp="13bffe07eb06s">Apply by {job.applyBy}</p>
          </div>
          <button type="button" onClick={apply} tabIndex={showBar ? 0 : -1} className="inline-flex h-11 shrink-0 items-center rounded-[7px] bg-brand px-5 text-[15px] font-semibold text-white" data-id="element-1884" data-mp="zac13i4zxc7o">
            Apply now
          </button>
        </div>
      </div>
    </main>;
}
function JobSection({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section data-id="element-1885" data-mp="2i6dp2ckkimoi">
      <h2 className="text-[22px] font-semibold tracking-[-0.02em] text-ink" data-id="element-1886" data-mp="2i63yamrp8bhr">{title}</h2>
      <div className="mt-3 space-y-4 text-[17px] leading-[1.7] text-ink-soft" data-id="element-1887" data-mp="24fidgsioprh8">{children}</div>
    </section>;
}
function BulletList({
  items,
  check
}: {
  items: string[];
  check?: boolean;
}) {
  return <ul className="space-y-2.5" data-id="element-1888" data-mp="1ujfefvj4q8yi">
      {items.map(item => <li key={item} className="flex gap-3" data-id="element-1889" data-mp="1zgnwzx86wf0a">
          {check ? <CheckIcon className="mt-1.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" data-id="element-1890" data-mp="1zeula3ipz4lf" /> : <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-hidden="true" data-id="element-1891" data-mp="1pizycw7gqncu" />}
          <span data-id="element-1892" data-mp="16u7chap07l6e">{item}</span>
        </li>)}
    </ul>;
}